import { Vector3 } from "three";
import { Obj } from "../internal";
import { MeasurePoint, MeasureHandler } from "./internal";
export class WorkerStats extends Obj {
    constructor() {
        super(...arguments);
        this.convergence = false;
        this.numObservations = 0;
        this.numCalcIterations = 0;
        this.calcexecuted = false;
    }
    print() {
        const str = "convergence: " +
            this.convergence +
            " numIterations: " +
            this.numCalcIterations +
            " numObservations: " +
            this.numObservations +
            " m0: " +
            this.m0 +
            " s_xyz: " +
            this.s_xyz;
        const css = "color:darkblue";
        console.log("%c\n\nCALC STATS: %s", css, str);
    }
    printDetails() {
        for (let i = 0; i < this.residuals.length; i += 2) {
            const j = Math.floor(i / 2);
            const str = "[" +
                j +
                "]: residuals: [" +
                this.residuals[i] +
                "," +
                this.residuals[i + 1] +
                "] Segment/Stream/Frame: " +
                this.segmentStreamFrame[j];
            const css = "color:blue;font-style:italic";
            console.log("%cCALC DETAIL STATS %s", css, str);
        }
    }
}
/**
 * Multi-View Measure point representation
 * @class MultiViewMeasurePoint
 * @extends Obj
 * @constructor
 */
export class MultiViewMeasurePoint extends MeasurePoint {
    /**
     * A MultiViewMeasurePoint is a Measurepoint calculated from multiple image observations
     * @param geoframe
     * @param calcupdate - is fired on every iteration in calculation process -> use this for progress bars etc...
     * @param calcfinished - is fired when calculation is finished
     * @param cbobject
     */
    constructor(geoframe, calcupdate, calcfinished, cbobject) {
        super();
        this.worker = null;
        this.scene = null;
        this.imageobservations = [];
        this.geoframe = geoframe;
        this.scene = geoframe.scene;
        this.imageobservations = [];
        //set up worker
        /* this.worker = new BundelWorker(); */
        this.worker = new Worker(new URL("./bundel.worker.js", import.meta.url));
        this.worker.onmessage = (e) => {
            this._workercb(e);
        };
        this.cbobject = cbobject ? cbobject : this;
        this.calcupdate = calcupdate;
        this.calcfinished = calcfinished;
        this.stats = new WorkerStats();
    }
    /**
     * Calculates the coordinates out of the mouse position
     */
    calcCoordinates(approxval) {
        this.approxval = approxval;
        this.startWorker();
    }
    /**
     * Start the Bündelblock-Worker
     */
    startWorker() {
        // prepare input data for worker
        // [X0, Y0, Z0, o, p, k, xs, ys, ck, w, proj]
        const inputdata = [];
        for (let i = 0; i < this.imageobservations.length; i++) {
            //pixel measurement accuracy = 1 pixel
            const weight = 81.0 / Math.pow(this.imageobservations[i].pixelsize, 2);
            inputdata.push([
                this.imageobservations[i].sensoreasting,
                this.imageobservations[i].sensornorthing,
                this.imageobservations[i].sensorheight,
                this.imageobservations[i].sensoromega,
                this.imageobservations[i].sensorphi,
                this.imageobservations[i].sensorkappa,
                this.imageobservations[i].x_imgcoor,
                this.imageobservations[i].y_imgcoor,
                this.imageobservations[i].focallength,
                weight,
                this.imageobservations[i].projection,
            ]);
        }
        //go!
        this.worker.postMessage({
            topic: "run",
            approxval: this.approxval,
            observations: JSON.stringify(inputdata),
        });
        this.stats.calcexecuted = true;
    }
    /**
     * The worker callback
     * @param e
     * @private
     */
    _workercb(e) {
        switch (e.data["topic"]) {
            case "result": {
                this.stats.convergence = e.data.convergence;
                this.stats.numCalcIterations = e.data["finalIterations"];
                this.stats.finalCorrection = e.data["correction"];
                this.stats.m0 = e.data["m0"];
                this.stats.numObservations = this.imageobservations.length;
                this.stats.residuals = e.data["residuals"];
                this.stats.s_xyz = e.data["s_xyz"];
                this.stats.segmentStreamFrame = [];
                for (let i = 0; i < this.imageobservations.length; i++) {
                    const seg = this.imageobservations[i].geoframe.segment;
                    const str = this.imageobservations[i].geoframe.stream;
                    const frm = this.imageobservations[i].geoframe.getFramenumber();
                    this.stats.segmentStreamFrame.push(seg + "/" + str + "/" + frm);
                }
                this.stats.print();
                this.stats.printDetails();
                this.Easting = e.data["result"][0];
                this.Northing = e.data["result"][1];
                this.Height = e.data["result"][2];
                const mh = this.scene.getMeasureHandler();
                const frame_coords = mh.worldToFrame([this.Easting, this.Northing, this.Height]);
                this.frameX = frame_coords.x;
                this.frameY = frame_coords.y;
                this.frameZ = frame_coords.z;
                const geoframe = this.scene.getCurrentGeoframe();
                const R_c0_w = MeasureHandler.getRotationMatrix((geoframe.omega * Math.PI) / 180, (geoframe.phi * Math.PI) / 180, (geoframe.kappa * Math.PI) / 180);
                const prec_frame_coords = new Vector3().fromArray(e.data.s_xyz);
                prec_frame_coords.applyMatrix4(R_c0_w.transpose());
                this.precFrameX = prec_frame_coords.x;
                this.precFrameY = prec_frame_coords.y;
                this.precFrameZ = prec_frame_coords.z;
                if (this.calcfinished)
                    this.calcfinished.call(this.cbobject, this.stats);
                break;
            }
            case "correction_update":
                if (this.calcupdate)
                    this.calcupdate(this.stats);
                break;
        }
    }
    addImageObservation(io) {
        this.imageobservations.push(io);
    }
    getStatistics() {
        return this.stats;
    }
    destroy() {
        this.worker = null;
    }
    /**
     * returns the point values as vec3 object.
     *
     * @return {Vector3}
     */
    getVec3() {
        const vec = new Vector3(this["Easting"], this["Northing"], this["Height"]);
        return vec;
    }
    /**
     * returns the point values as vec3 object.
     *
     * @return {MeasurePoint}
     */
    copy() {
        const pointcopy = new MultiViewMeasurePoint(this.geoframe, this.calcupdate, this.calcfinished);
        pointcopy.imageobservations = this.imageobservations;
        pointcopy.stats = this.stats;
        return pointcopy;
    }
    /**
     * checks if the measurement is in Range
     */
    checkCoordinates() {
        this.warntext = "";
        this.warning = false;
        if (!this.isValid()) {
            this.warning = true;
            this.warntext = "Inavlid Values in Northing/Easting/Height";
        }
    }
}
//# sourceMappingURL=multiviewmeasurepoint.js.map